/**
 * Tells the Android side whether anything is depending on us in the background.
 *
 * The anchor drag check runs in JavaScript, in the WebView (backgroundAlarms.ts,
 * driven by BackgroundGeolocationRunner). That is fine right up until Android
 * reclaims the renderer to free memory, which is exactly what it does to a
 * backgrounded WebGL map at three in the morning. The moment it does, the watch
 * stops. Not pauses: stops. And what is left on the phone is a persistent
 * notification still saying we are watching, which is worse than no notification
 * at all, because it is a wrong answer to a question about safety.
 *
 * Native code is the only thing still running at that point, and it cannot see
 * into the web app to know whether a watch was on. So we leave it a note. One
 * small file in the app's own data directory, the same folder MainActivity
 * already writes renderer-deaths.log to, holding the reason a watch is active, or
 * empty when nothing is. MainActivity reads it when the renderer dies and, if
 * there was a watch, says so out loud.
 *
 * A file rather than a bridge call because by the time it matters the bridge is
 * the thing that has died. It is written on change only, not on a timer, so it
 * costs nothing to keep current.
 *
 * This is the honest half of the fix, not the whole one. It does not keep the
 * watch running; it stops the watch failing silently. Moving the drag check
 * itself somewhere it survives is the larger job.
 */
import { Capacitor } from '@capacitor/core';

const FLAG_FILE = 'active-watch.flag';

/** The last value written, so an unchanged reason does not rewrite the file. */
let lastWritten: string | null = null;

/**
 * Record what is depending on us in the background, or null when nothing is.
 *
 * Never throws and never rejects: a diagnostic that can fail on the safety path
 * would be worse than the thing it is diagnosing.
 */
export async function writeWatchFlag(reason: string | null): Promise<void> {
  const text = reason ?? '';
  if (text === lastWritten) return;
  if (!Capacitor.isNativePlatform()) {
    lastWritten = text;
    return;
  }
  try {
    const { Filesystem, Directory, Encoding } = await import('@capacitor/filesystem');
    await Filesystem.writeFile({
      path: FLAG_FILE,
      // Directory.Data is the app's own files dir, which is where MainActivity
      // looks. Same folder, both sides, same as the renderer death log.
      directory: Directory.Data,
      encoding: Encoding.UTF8,
      data: text,
    });
    lastWritten = text;
  } catch {
    // Nothing to do and nothing worth saying. Worst case the native side does
    // not know a watch was on and stays quiet, which is where we were before.
  }
}

/** Test seam: forget what we think is on disk. */
export function resetWatchFlagCache(): void {
  lastWritten = null;
}
